import { syncState } from "@legendapp/state";
import { useObserve, useValue } from "@legendapp/state/react";
import { Spinner } from "@repo/ui/ui/spinner";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { datasources$ } from "~/stores/datasources";

/**
 * Resume fallback for `/onboard/boards`. Hit when the user lands on the bare
 * path (refresh during the boards step or via the resume route). Reactively
 * waits for `datasources$` to hydrate, then forwards to the most recently
 * created datasource's boards page. If the org has no datasources at all,
 * advances to the done step so the wizard isn't dead-ended.
 */
const ResumeBoards = () => {
  const navigate = useNavigate();
  const isLoaded = useValue(syncState(datasources$).isLoaded);

  useObserve(() => {
    if (!syncState(datasources$).isLoaded.get()) return;

    const items = datasources$.get();
    const list = items ? Object.values(items) : [];
    const latest = [...list].sort((a, b) => {
      const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bTime - aTime;
    })[0];

    queueMicrotask(() => {
      if (latest) {
        navigate({
          to: "/onboard/boards/$datasourceId",
          params: { datasourceId: latest.id },
          replace: true,
        });
      } else {
        navigate({ to: "/onboard/done", replace: true });
      }
    });
  });

  return (
    <div className="flex min-h-96 flex-1 items-center justify-center">
      <Spinner className="size-6" />
      {!isLoaded && <span className="sr-only">Loading datasources</span>}
    </div>
  );
};

export const Route = createFileRoute("/onboard/boards/")({
  component: ResumeBoards,
});
